import * as Sentry from "@sentry/browser";
import * as Core from "@zeller-public/payments-sdk-core";
import { safe } from "pb.safe";
import * as v from "valibot";

import { onRequestCancel } from "./on-request-cancel";
import { onRequestQuery } from "./on-request-query";
import { logger } from "./shared/logger";
import { Debug, Resolve } from "./transport/response";

export * from "./transport/response";
export * from "./on-cancel";

// Only to be used in index.tsx entry-point of bundle.
export function Transport_attach() {
	// webview (injected invocation)
	window.onMessage = onRequest;

	// iframe (window event listener)
	window.addEventListener("message", (event) => {
		// only listen to external messages i.e. from the iframe parent
		if (event.origin !== window.location.origin) {
			onRequest(event.data);
		}
	});

	// microsoft webviews (window event listener)
	// guard the method, not just the object: some Chrome hosts expose a partial
	// window.chrome.webview without addEventListener
	if (typeof window?.chrome?.webview?.addEventListener === "function") {
		window.chrome.webview.addEventListener("message", (event) => {
			onRequest(event.data);
		});
	}

	// Warn if a response is still pending when the page is hidden (tab
	// backgrounded, WebView dismissed). window._request is only cleared once a
	// Result/ResultError is posted (see post-response.ts -> onRequestQueryEnd),
	// so a truthy value here means the integrator has not yet received its final
	// response.
	window.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden" && window._request) {
			logger.warn("transport: visibilitychange - response pending on hide", {
				type: window._request.query.$type,
			});
			void Sentry.flush(0);
		}
	});
}

export function onRequest(messageUnknown: unknown): void {
	Debug({ message: "handling request" });

	if (typeof messageUnknown !== "string") {
		return void logger.debug("transport: on request unknown", {
			message: messageUnknown,
		});
	}

	const request = safe(() =>
		v.parse(Core.Protocol.Request, JSON.parse(messageUnknown)),
	);
	if (request instanceof Error) {
		logger.error("transport: request error", {
			request: messageUnknown,
			errorName: request.name,
			errorMessage: request.message,
			errorStack: request.stack,
			errorCause: request.cause,
		});

		return void Resolve.Error("Operation Request Invalid", {
			message: "The request could not be parsed.",
		});
	} else {
		logger.debug("transport: request", { $type: request.$type });
	}

	if (request.$type === "Query") {
		onRequestQuery(request);
	} else if (request.$type === "Cancel") {
		onRequestCancel();
	}
}
